/* Law Firms UI: the shared portfolio look (cards, light and dark, works on phones).
   Material UI components take their colours from the theme in App.tsx. */

/* Colours: light by default, dark when the device asks for it */
:root {
  --bg: #f8f4f5;
  --surface: #ffffff;
  --text: #24181b;
  --muted: #6e5a5f;
  --border: #eadde0;
  --accent: #8c2335;
  --accent-strong: #6a1726;
  --accent-soft: #f6e6e9;
  --hero-from: #b23a50;
  --hero-to: #3d0b15;
  --shadow: 0 1px 2px rgba(50, 15, 25, 0.06), 0 4px 16px rgba(50, 15, 25, 0.06);
  --radius: 14px;
  --mono: ui-monospace, "Cascadia Code", Consolas, monospace;
  color-scheme: light dark;
}

@media (prefers-color-scheme: dark) {
  :root {
    --bg: #161012;
    --surface: #20181b;
    --text: #f1e6e8;
    --muted: #bba4a9;
    --border: #3a2a2f;
    --accent: #f08da0;
    --accent-strong: #f7bcc7;
    --accent-soft: #3d1c24;
    --hero-from: #8c2335;
    --hero-to: #22060c;
    --shadow: none;
  }
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.6;
  color: var(--text);
  background: var(--bg);
}

a {
  color: var(--accent);
  overflow-wrap: anywhere;
}

a:hover {
  color: var(--accent-strong);
}

code {
  font-family: var(--mono);
  font-size: 0.9em;
  background: var(--accent-soft);
  padding: 0.1em 0.35em;
  border-radius: 5px;
  word-break: break-word;
}

.wrap {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 16px;
}

/* Top bar */
.topbar {
  background: var(--hero-to);
  color: #fff;
}

.topbar .wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 18px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.brand {
  font-weight: 700;
  margin-right: auto;
}

.topbar a {
  color: #fff;
  text-decoration: none;
  font-size: 0.95rem;
  padding: 4px 12px;
  border-radius: 999px;
  opacity: 0.85;
}

.topbar a:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.12);
}

/* Header */
.hero {
  background: linear-gradient(135deg, var(--hero-from), var(--hero-to));
  color: #fff;
  padding: 40px 0 44px;
}

.hero h1 {
  margin: 0 0 8px;
  font-size: clamp(1.8rem, 5vw, 2.5rem);
  line-height: 1.15;
}

.eyebrow {
  margin: 0 0 8px;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.85;
}

.lead {
  margin: 0;
  max-width: 760px;
  font-size: 1.1rem;
  opacity: 0.95;
}

/* Cards */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(420px, 100%), 1fr));
  gap: 20px;
  margin-top: -20px;
  padding-bottom: 32px;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 22px;
  min-width: 0;
}

.card-wide {
  grid-column: 1 / -1;
}

.card h2 {
  margin: 0 0 6px;
  font-size: 1.25rem;
}

.card > :last-child {
  margin-bottom: 0;
}

.card ul {
  padding-left: 1.3em;
}

.card li + li {
  margin-top: 6px;
}

.muted {
  color: var(--muted);
}

.small {
  font-size: 0.9rem;
}

.notice {
  border-radius: 10px;
  padding: 12px 16px;
  margin: 12px 0 0;
  background: var(--accent-soft);
  border: 1px solid var(--accent);
}

/* Add form: two fields a row on wide screens */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 16px;
  margin: 16px 0;
}

/* Law firms table (Material UI): sortable headers, and rows stack into cards on phones */
.firms-table th {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}

.firms-table .sort-button {
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.firms-table .sort-button:hover,
.firms-table .sort-button.sorted {
  color: var(--accent);
}

.firms-table .mono {
  font-family: var(--mono);
  font-size: 0.85rem;
}

@media (max-width: 700px) {
  /* only the sortable headers stay, as a sort bar */
  .firms-table thead tr {
    display: flex;
    gap: 18px;
  }

  .firms-table thead th {
    display: block;
    padding: 0 0 8px;
  }

  .firms-table thead th.nosort {
    display: none;
  }

  .firms-table tfoot tr,
  .firms-table tfoot td {
    display: block;
    padding: 0;
  }

  .firms-table .MuiTablePagination-toolbar {
    flex-wrap: wrap;
    justify-content: flex-end;
    padding: 4px 0 0;
  }

  .firms-table .MuiTablePagination-spacer {
    display: none;
  }

  .firms-table tbody tr {
    display: block;
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
  }

  .firms-table tbody td {
    display: grid;
    grid-template-columns: 6.5em 1fr;
    gap: 8px;
    padding: 3px 0;
    border: 0;
    text-align: left;
    overflow-wrap: anywhere;
  }

  .firms-table tbody td::before {
    content: attr(data-label);
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--muted);
  }

  .firms-table tbody td.full {
    display: block;
  }

  .firms-table tbody td.full::before {
    content: none;
  }

  .firms-table tfoot td {
    border: 0;
  }
}

.sort-hint {
  margin: 0 0 10px;
}

/* Footer */
.footer {
  border-top: 1px solid var(--border);
  padding: 20px 0 28px;
  font-size: 0.9rem;
  color: var(--muted);
}
